import { useEffect, useState } from "react";
import axios from "axios";
import { X, Check, Loader2 } from "lucide-react";

// Join-the-waitlist modal for FULL events. Notify-style: the devotee is
// emailed (in joining order) as soon as seats free up, then registers
// normally — seats themselves are never held here.
export default function WaitlistModal({ isOpen, onClose, scheduleId, eventName }) {
  const base = import.meta.env.VITE_APP_API_BASE_URL || "";
  const apiRoot = base.replace(/\/scheduleRegistration\/?$/, "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [partySize, setPartySize] = useState(1);
  const [state, setState] = useState("ready"); // ready | submitting | done | error
  const [message, setMessage] = useState("");
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setEmail("");
      setPhone("");
      setPartySize(1);
      setState("ready");
      setMessage("");
      setPosition(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setMessage("Please share your name and email.");
      setState("error");
      return;
    }
    try {
      setState("submitting");
      setMessage("");
      const r = await axios.post(`${apiRoot}/waitlist`, {
        scheduleId,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        partySize,
      });
      setPosition(r.data?.data?.position || null);
      setMessage(r.data?.message || "You are on the waitlist.");
      setState("done");
    } catch (err) {
      setMessage(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
      setState("error");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="bg-gradient-to-r from-[#5a0000] to-[#8a0000] text-white p-4 rounded-t-lg flex justify-between items-center">
          <h3 className="text-lg font-semibold">Join the waitlist</h3>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {state === "done" ? (
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-1 mt-0.5">
                <Check size={16} className="text-green-600" />
              </div>
              <div>
                <p className="font-medium">
                  You are on the waitlist{position ? ` — #${position}` : ""}
                </p>
                <p className="text-sm mt-1">
                  If seats open up, we will email you right away. Openings are
                  shared in order of joining.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm text-gray-600">
                {eventName ? `${eventName} is` : "This event is"} currently full.
                Leave your details and we will email you the moment seats open.
              </p>
              {message && state === "error" && (
                <p className="text-sm text-red-600">{message}</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full name <span className="text-red-500">*</span>
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
                  autoComplete="name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
                  autoComplete="email"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone (optional)
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
                    autoComplete="tel"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of people
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={25}
                    value={partySize}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Empty is allowed while typing; clamped on blur/submit.
                      if (raw === "") {
                        setPartySize("");
                        return;
                      }
                      const parsed = parseInt(raw, 10);
                      if (!isNaN(parsed)) setPartySize(parsed);
                    }}
                    onBlur={() => {
                      const parsed = parseInt(partySize || "1", 10);
                      setPartySize(isNaN(parsed) ? 1 : Math.max(1, Math.min(25, parsed)));
                    }}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={state === "submitting"}
                className="w-full bg-[#5a0000] hover:bg-[#8a0000] text-white py-3 px-4 rounded-md transition-colors disabled:opacity-70 flex justify-center items-center font-medium"
              >
                {state === "submitting" ? (
                  <>
                    <Loader2 size={18} className="animate-spin mr-2" />
                    Joining…
                  </>
                ) : (
                  "Join the waitlist"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
